import type { CreateInvitationRequest } from '@/api/invitations/invitationMutations';
import { createInvitationMutation } from '@/api/invitations/invitationMutations';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/common/Accordion';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { useFormMutation } from '@/hooks/useFormMutation';
import type { InvitePatientFormValues } from '@/validation/patientValidator';
import { invitePatientSchema } from '@/validation/patientValidator';
import { useFormik } from 'formik';
import { toFormikValidate } from 'zod-formik-adapter';
import { Card } from '../DashboardCard';

export default function InvitePatient() {
  const formik = useFormik<InvitePatientFormValues>({
    initialValues: {
      name: '',
      surname: '',
      email: '',
    },
    validate: toFormikValidate(invitePatientSchema),
    onSubmit: async (values) => {
      const payload: CreateInvitationRequest = {
        email: values.email,
        name: values.name,
        surname: values.surname,
        role: 'Customer',
      };
      await createInvitation.mutateAsync(payload);
      formik.resetForm();
    },
  });

  const createInvitation = useFormMutation({
    ...createInvitationMutation(),
    formik,
  });

  return (
    <AccordionItem
      value="Invite patient"
      className="bg-background rounded-2xl border-none p-5.5"
    >
      <AccordionTrigger className="text-table-foreground text-xl font-normal hover:no-underline">
        Invite patient
      </AccordionTrigger>
      <AccordionContent>
        <Card>
          <form
            onSubmit={formik.handleSubmit}
            className="flex flex-col gap-4 sm:flex-row"
          >
            <Input
              id="name"
              name="name"
              label="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.name}
              touched={formik.touched.name}
            />
            <Input
              id="surname"
              name="surname"
              label="Surname"
              value={formik.values.surname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.surname}
              touched={formik.touched.surname}
            />

            <Input
              id="email"
              name="email"
              type="email"
              label="Email Address"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.email}
              touched={formik.touched.email}
            />

            <div>
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={createInvitation.isPending}
              >
                {createInvitation.isPending ? 'Sending...' : 'Send Invitation'}
              </Button>
            </div>
          </form>
        </Card>
      </AccordionContent>
    </AccordionItem>
  );
}
